import { useRef, useState } from 'react';
import { callApi } from './api';
import { getStoredKanriCodeToken, saveKanriCodeToken } from './kanriCodeToken';
import { buildFukumeishoWordBlob } from './fukumeishoExport';
import { downloadBlob } from './wordExport';
import FukumeishoPreview from './FukumeishoPreview';

function todayJP() {
  const d = new Date();
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

const FIELD_DEFS = [
  { key: 'trainingName', label: '研修名' },
  { key: 'trainingPlace', label: '研修場所' },
  { key: 'datetime', label: '日時' },
  { key: 'submitDate', label: '提出日' },
  { key: 'jobType', label: '職種' },
  { key: 'name', label: '氏名' },
];

/** トップページ隅の非公開機能「管理者コード」。研修参加メモをAIでまとめ、復命書としてWord出力する。 */
export default function KanriCodeScreen({ onBack }) {
  const [token, setToken] = useState(() => getStoredKanriCodeToken());
  const [password, setPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const unlockingRef = useRef(false);

  const [fields, setFields] = useState({
    trainingName: '', trainingPlace: '', datetime: '',
    submitDate: todayJP(), jobType: '総務', name: '本山将也',
  });
  const [inputText, setInputText] = useState('');
  const [resultText, setResultText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  const [promptText, setPromptText] = useState(null); // null=未読み込み
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptSaved, setPromptSaved] = useState(false);
  const [promptError, setPromptError] = useState('');

  async function handleUnlock(e) {
    e.preventDefault();
    if (!password || unlockingRef.current) return;
    unlockingRef.current = true;
    setUnlocking(true);
    setUnlockError('');
    try {
      const data = await callApi('kanri_code_login', { password });
      saveKanriCodeToken(data.token);
      setToken(data.token);
      setPassword('');
    } catch (err) {
      setUnlockError(err.message);
    } finally {
      unlockingRef.current = false;
      setUnlocking(false);
    }
  }

  function updateField(key, value) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  async function handleGenerate() {
    if (!inputText.trim() || generating) return;
    setGenerating(true);
    setGenerateError('');
    try {
      const data = await callApi('kanri_code_generate', { inputText: inputText.trim() }, token);
      setResultText(data.text);
    } catch (err) {
      if (err.code === 'KANRI_CODE_TOKEN_INVALID') setToken(null);
      setGenerateError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleTogglePromptEditor() {
    const opening = !promptEditorOpen;
    setPromptEditorOpen(opening);
    if (opening && promptText === null) {
      setPromptLoading(true);
      setPromptError('');
      try {
        const data = await callApi('kanri_code_prompt_get', {}, token);
        setPromptText(data.text);
      } catch (err) {
        if (err.code === 'KANRI_CODE_TOKEN_INVALID') setToken(null);
        setPromptError(err.message);
      } finally {
        setPromptLoading(false);
      }
    }
  }

  async function handleSavePrompt() {
    if (promptSaving || !promptText?.trim()) return;
    setPromptSaving(true);
    setPromptError('');
    try {
      await callApi('kanri_code_prompt_save', { text: promptText }, token);
      setPromptSaved(true);
      setTimeout(() => setPromptSaved(false), 2000);
    } catch (err) {
      if (err.code === 'KANRI_CODE_TOKEN_INVALID') setToken(null);
      setPromptError(err.message);
    } finally {
      setPromptSaving(false);
    }
  }

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    setDownloadError('');
    try {
      const blob = await buildFukumeishoWordBlob(fields, resultText);
      downloadBlob(blob, `復命書_${fields.trainingName || '無題'}.docx`);
    } catch (err) {
      setDownloadError('Wordファイルの作成に失敗しました。');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="workspace is-wide">
      <div className="workspace-head reveal r1">
        <button className="back-link" type="button" onClick={onBack}>← ホームへ戻る</button>
        <h1 className="workspace-title">管理者コード</h1>
      </div>

      {!token && (
        <form className="editor-card reveal r2" onSubmit={handleUnlock}>
          <p className="admin-lock-note">この機能を開くには、パスワードが必要です。</p>
          <div className="admin-lock-row">
            <input
              type="password" className="admin-password-input" placeholder="パスワード" autoComplete="off"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setUnlockError(''); }}
              disabled={unlocking}
            />
            <button className="enter admin-unlock-btn" type="submit" disabled={!password || unlocking}>
              {unlocking ? '確認中…' : '開く'}
            </button>
          </div>
          {unlockError && <p className="pin-error" role="alert">{unlockError}</p>}
        </form>
      )}

      {token && (
        <div className="gen-stack">
          <div className="notice reveal r2">
            利用者様・ご家族のお名前など、個人情報は入力しないでください。
          </div>

          <div className="editor-card reveal r2">
            <h2 className="admin-dept-title">研修のメモを貼り付け</h2>
            <textarea
              className="editor-textarea"
              placeholder="研修で聞いた内容、気づいた点などを自由に書いてください（箇条書きでも構いません）"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              disabled={generating}
            />
            <div className="admin-save-row">
              <button
                className="enter admin-save-btn" type="button"
                disabled={!inputText.trim() || generating}
                onClick={handleGenerate}
              >
                {generating ? 'まとめています…' : 'AIで復命書の本文にまとめる'}
              </button>
              <button type="button" className="admin-link" onClick={handleTogglePromptEditor}>
                {promptEditorOpen ? '編集を閉じる' : '⚙ 指示を編集する'}
              </button>
            </div>
            {generateError && <p className="pin-error" role="alert">{generateError}</p>}

            {promptEditorOpen && (
              <div className="word-form">
                <p className="word-form-note">
                  「AIで本文にまとめる」を押したときにGeminiへ渡す指示文です。書き方や注目してほしい点などを自由に指定できます。
                </p>
                {promptLoading && <p className="workspace-loading">読み込んでいます…</p>}
                {!promptLoading && promptText !== null && (
                  <>
                    <textarea
                      className="editor-textarea admin-prompt-textarea"
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      disabled={promptSaving}
                    />
                    <div className="admin-save-row">
                      <button
                        className="enter admin-save-btn" type="button"
                        disabled={promptSaving || !promptText.trim()}
                        onClick={handleSavePrompt}
                      >
                        {promptSaving ? '保存中…' : '指示を保存'}
                      </button>
                      {promptSaved && <span className="admin-saved-label">保存しました</span>}
                    </div>
                  </>
                )}
                {promptError && <p className="pin-error" role="alert">{promptError}</p>}
              </div>
            )}
          </div>

          <div className="gen-layout reveal r3">
            <div className="result-card">
              <div className="result-head">
                <h2 className="result-title">入力フォーム</h2>
              </div>
              <div className="word-form-grid">
                {FIELD_DEFS.map((f) => (
                  <label className="word-form-field" key={f.key}>
                    <span>{f.label}</span>
                    <input
                      type="text" value={fields[f.key]}
                      onChange={(e) => updateField(f.key, e.target.value)}
                    />
                  </label>
                ))}
              </div>
              <label className="editor-label" htmlFor="fukumeisho-body">本文</label>
              <textarea
                id="fukumeisho-body"
                className="editor-textarea"
                placeholder="AIでまとめた本文がここに表示されます。直接編集もできます。"
                value={resultText}
                onChange={(e) => setResultText(e.target.value)}
              />
            </div>

            <div className="preview-card">
              <div className="preview-card-head">
                <h2 className="result-title">Wordプレビュー</h2>
                <button className="enter word-download-btn" type="button" disabled={downloading} onClick={handleDownload}>
                  {downloading ? '作成中…' : 'Wordでダウンロード'}
                </button>
              </div>
              {downloadError && <p className="pin-error" role="alert">{downloadError}</p>}
              <FukumeishoPreview
                fields={fields}
                onFieldChange={updateField}
                bodyText={resultText}
                onBodyChange={setResultText}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
